import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import apiClient from '../api/client';
import { useAuth } from './AuthContext';

const SubCalendarContext = createContext({ subCalendars: [], loaded: false, loadFailed: false, refetch: () => {} });

export function SubCalendarProvider({ children }) {
  const { user } = useAuth();
  const [subCalendars, setSubCalendars] = useState([]);
  // `loaded` lets a screen tell "not here yet" from "you have none" — without
  // it the label row was absent for a moment and then appeared, shoving the
  // rest of the page down.
  const [loaded, setLoaded] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);

  const fetchSubCalendars = useCallback(async () => {
    if (!user) { setSubCalendars([]); setLoaded(false); setLoadFailed(false); return; }
    try {
      const res = await apiClient.get('/sub-calendars');
      setSubCalendars(res.data || []);
      setLoaded(true);
      setLoadFailed(false);
    } catch {
      // Keep whatever we already had. Replacing the list with [] on a failed
      // read made the colour filters vanish from Today and the label row vanish
      // from the event form — the app telling the user their labels were gone.
      setLoadFailed(true);
    }
  }, [user]);

  useEffect(() => { fetchSubCalendars(); }, [fetchSubCalendars]);

  async function createSubCalendar(data) {
    const res = await apiClient.post('/sub-calendars', data);
    await fetchSubCalendars();
    return res.data;
  }

  async function updateSubCalendar(id, data) {
    const res = await apiClient.put(`/sub-calendars/${id}`, data);
    await fetchSubCalendars();
    return res.data;
  }

  // The colour of your OWN calendar: off unless asked for, because tinting
  // everything you own would make the tint stop meaning "somebody else's".
  // Creating and removing the label is the server's job — here we only ask.
  async function setOwnColour(enabled, color) {
    const res = await apiClient.put('/sub-calendars/own-colour', { enabled: !!enabled, color: color || undefined });
    await fetchSubCalendars();
    return res.data;          // so the screen can open the colour picker on it
  }

  async function deleteSubCalendar(id) {
    await apiClient.delete(`/sub-calendars/${id}`);
    await fetchSubCalendars();
  }

  return (
    <SubCalendarContext.Provider value={{
      subCalendars,
      loaded,
      loadFailed,
      refetch: fetchSubCalendars,
      createSubCalendar,
      updateSubCalendar,
      deleteSubCalendar,
      setOwnColour,
    }}>
      {children}
    </SubCalendarContext.Provider>
  );
}

export function useSubCalendars() {
  return useContext(SubCalendarContext);
}
